/**
 * AI 自动选模（Alice 页面 autoRouting 的复刻）：编辑自动选模策略，校验与试算后发布，查看路由统计与影子决策。
 *
 * 背景：调用方把 model 写成「auto」（对外名可改）时，数据面按场景策略（固定模型，或按内容规则分经济档与质量档）选出真实模型；
 *   规则命中不到时回落到场景的默认档；同一会话选过质量档后会绑定一段时间，避免对话中途换模型（绑定键含 binding_epoch，
 *   改 epoch 即让全部绑定作废）。策略分 shadow（只记录不生效）和 enforce（生效）两种模式。
 * 设计意图：
 *   - 策略以 JSON 编辑（Alice 同款深色编辑器），发布前必须先「校验与试算」：后端用与数据面同一份决策函数逐条算出每个样本会选哪个模型，
 *     并列出引用了不存在或不可路由模型的问题，试算不写库。
 *   - 影子决策（shadow_rules）与生效决策并行计算，只记录对比，不影响调用；这里看一致率、成功率与延迟分位数来决定要不要转 enforce。
 * 关键约束：只有 admin 能发布、改影子采样；发布后生成新配置版本，数据面几秒内换上。本仓没有 Alice 的 memory-gate 影子，不提供该入口。
 */
import { IconPlayerPlay, IconRefresh, IconRocket } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { catalogApi, type AutoPreview, type ShadowConfig } from '@ap/api-client/admin-catalog'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { A } from '@ap/i18n/admin'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Notice, fmtDateTime, useLoad, useToast } from '@ap/ui'
import { OpsPage } from '../../alice/kit.tsx'
import { InfoBox, SummaryCard, SummaryGrid, fmtMs } from '../../alice/kit-catalog.tsx'
import { useAdmin } from '../../session.tsx'

const DEFAULT_SAMPLES = ['hello', 'def add(a, b):\n    return a + b', 'Please write a detailed design review for the payment service.']

/** 决策耗时多在亚毫秒级，不足 1ms 时保留三位小数，避免显示成 0ms。 */
const latText = (ms: number | null) => (ms == null ? '—' : ms < 1 ? `${ms.toFixed(3)}ms` : fmtMs(ms))

const pctText = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)

export function AutoRouting() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const doc = useLoad(() => catalogApi.autoPolicy(), [])
  const [hours, setHours] = useState(24)
  const stats = useLoad(() => catalogApi.autoStats(hours), [hours])
  const shadow = useLoad(() => catalogApi.shadowSummary(hours), [hours])
  const shadowCfg = useLoad(() => catalogApi.shadowConfig(), [])

  const [text, setText] = useState('')
  const [samples, setSamples] = useState(DEFAULT_SAMPLES.join('\n---\n'))
  const [preview, setPreview] = useState<AutoPreview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [cfg, setCfg] = useState<ShadowConfig>({ enabled: false, sample_rate: 0 })
  const [dirtyPolicy, setDirtyPolicy] = useState(false)

  // 服务端策略载入或保存后，同步编辑器；用户有未保存修改时不覆盖
  useEffect(() => {
    if (doc.data && !dirtyPolicy) setText(JSON.stringify(doc.data.policy, null, 2))
  }, [doc.data, dirtyPolicy])
  useEffect(() => {
    if (shadowCfg.data) setCfg({ enabled: shadowCfg.data.enabled, sample_rate: shadowCfg.data.sample_rate })
  }, [shadowCfg.data])

  const parse = (): unknown => {
    try {
      return JSON.parse(text)
    } catch {
      throw new Error(t(G.autoRouting.badJson))
    }
  }
  const sampleList = () =>
    samples
      .split(/\n-{3,}\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20)

  const run = async () => {
    setBusy('preview')
    setError('')
    try {
      setPreview(await catalogApi.previewAutoPolicy(parse(), sampleList()))
    } catch (e) {
      console.error('[auto-routing] 试算失败', e)
      setPreview(null)
      setError(e instanceof Error && !('code' in e) ? e.message : errorTextCat(e, locale))
    } finally {
      setBusy('')
    }
  }

  const publish = async () => {
    setBusy('publish')
    setError('')
    try {
      const r = await catalogApi.putAutoPolicy(parse())
      toast.ok(t(G.autoRouting.published, { v: r.config_version }))
      setDirtyPolicy(false)
      setPreview(null)
      doc.reload()
      shadowCfg.reload()
    } catch (e) {
      console.error('[auto-routing] 发布失败', e)
      setError(e instanceof Error && !('code' in e) ? e.message : errorTextCat(e, locale))
    } finally {
      setBusy('')
    }
  }

  const saveCfg = async () => {
    setBusy('cfg')
    try {
      const r = await catalogApi.putShadowConfig(cfg)
      toast.ok(t(G.autoRouting.cfgSaved, { v: r.config_version }))
      shadowCfg.reload()
    } catch (e) {
      console.error('[auto-routing] 保存影子采样失败', e)
      toast.error(errorTextCat(e, locale))
    } finally {
      setBusy('')
    }
  }

  const p = doc.data?.policy
  const scenes = p ? Object.entries(p.scenes) : []
  const enforce = scenes.filter(([, s]) => s.mode === 'enforce').length
  /** 决策原因：fixed、default、sticky、rule:<id>，后面可叠加 -shadow、-fallback、-unroutable 后缀。 */
  const reasonText = (r: string) => {
    let base = r
    const sfx: string[] = []
    for (;;) {
      const m = /-(shadow|fallback|unroutable)$/.exec(base)
      if (!m) break
      sfx.unshift(m[1])
      base = base.slice(0, m.index)
    }
    const head = base.startsWith('rule:') ? t(G.autoRouting.ruleHit, { id: base.slice(5) }) : G.autoRouting.reason[base] ? t(G.autoRouting.reason[base]) : base
    return [head, ...sfx.map((x) => t(G.autoRouting.reasonSfx[x]))].join(' · ')
  }
  const shadowEvents = (shadow.data?.data ?? []).reduce((s, r) => s + r.events, 0)

  return (
    <OpsPage eyebrow="AUTO ROUTING" title={t(A.nav.autoRouting)} desc={t(G.autoRouting.desc)} chip={t(G.autoRouting.chip)}>
      <SummaryGrid>
        <SummaryCard
          label={t(G.autoRouting.sPolicy)}
          value={doc.data ? t(doc.data.source === 'saved' ? G.autoRouting.sourceSaved : G.autoRouting.sourceDefault) : '—'}
          hint={doc.data ? `${doc.data.policy_version ? `v${doc.data.policy_version.slice(0, 8)}` : t(G.autoRouting.notSaved)}${doc.data.updated_at ? ` · ${fmtDateTime(doc.data.updated_at, locale)}` : ''}` : undefined}
        />
        <SummaryCard label={t(G.autoRouting.sScenes)} value={scenes.length} hint={t(G.autoRouting.sScenesHint, { a: enforce, b: scenes.length - enforce })} />
        <SummaryCard label={t(G.autoRouting.sRequests)} value={(stats.data?.total ?? 0).toLocaleString()} hint={t(G.autoRouting.sRequestsHint, { h: hours })} />
        <SummaryCard label={t(G.autoRouting.sShadow)} value={shadowEvents.toLocaleString()} hint={t(G.autoRouting.sShadowHint, { h: hours })} />
      </SummaryGrid>

      <InfoBox>
        <div>{t(G.autoRouting.note)}</div>
        {doc.data && doc.data.chat_models.length > 0 && (
          <div style={{ marginTop: 6, color: 'var(--text-tertiary)' }}>
            {t(G.autoRouting.available)} <span className="mono">{doc.data.chat_models.join(', ')}</span>
          </div>
        )}
      </InfoBox>
      {!isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="accent">{t(G.common.readOnly)}</Notice>
        </div>
      )}
      {doc.error ? <div className="cat-banner error">{t(G.common.loadFailed, { msg: errorTextCat(doc.error, locale) })}</div> : null}

      <div className="table-wrap" style={{ marginBottom: 16 }}>
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t(G.autoRouting.editorTitle)}</div>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(G.autoRouting.editorHint)}</span>
          <span style={{ flex: 1 }} />
          <button
            className="btn btn-secondary btn-sm"
            disabled={!dirtyPolicy}
            onClick={() => {
              setDirtyPolicy(false)
              setPreview(null)
              setError('')
              if (doc.data) setText(JSON.stringify(doc.data.policy, null, 2))
            }}
          >
            <IconRefresh size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {t(G.autoRouting.reset)}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={busy !== '' || !text.trim()} onClick={run}>
            <IconPlayerPlay size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {t(G.autoRouting.preview)}
          </button>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" disabled={busy !== '' || !text.trim()} onClick={publish}>
              <IconRocket size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(G.autoRouting.publish)}
            </button>
          )}
        </div>
        <div style={{ padding: 18 }}>
          {error && <div className="cat-banner error">{error}</div>}
          <textarea
            className="cat-dark-editor"
            spellCheck={false}
            readOnly={!isAdmin}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setDirtyPolicy(true)
            }}
          />
          <label className="cat-field-label" style={{ marginTop: 14 }}>
            {t(G.autoRouting.samples)}
          </label>
          <textarea
            className="form-input mono"
            style={{ width: '100%', minHeight: 90, fontSize: 12 }}
            value={samples}
            onChange={(e) => setSamples(e.target.value)}
          />
          {preview && (
            <div style={{ marginTop: 14 }}>
              {preview.ok ? (
                <div className="cat-banner" style={{ background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid #bbf7d0' }}>
                  {t(G.autoRouting.previewOk)}
                </div>
              ) : (
                <div className="cat-banner error">{t(G.autoRouting.previewFail, { err: preview.error })}</div>
              )}
              {preview.unroutable_models && preview.unroutable_models.length > 0 && (
                <div className="cat-banner warning">
                  {t(G.autoRouting.unroutable)} <span className="mono">{preview.unroutable_models.join(', ')}</span>
                </div>
              )}
              {preview.decisions && preview.decisions.length > 0 && (
                <div className="table-inner" style={{ border: '1px solid var(--border-medium)', borderRadius: 8 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t(G.autoRouting.colScene)}</th>
                        <th>{t(G.autoRouting.colSample)}</th>
                        <th>{t(G.autoRouting.colLevel)}</th>
                        <th>{t(G.autoRouting.colModel)}</th>
                        <th>{t(G.autoRouting.colReason)}</th>
                        <th>{t(G.autoRouting.colMode)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.decisions.map((d, i) => (
                        <tr key={i}>
                          <td className="mono">{d.scene_id}</td>
                          <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.sample}>
                            {d.sample}
                          </td>
                          <td>
                            <span className={`badge ${d.level === 'quality' ? 'badge-info' : 'badge-ok'}`}>{t(d.level === 'quality' ? G.autoRouting.levelQuality : G.autoRouting.levelEconomy)}</span>
                          </td>
                          <td className="mono">{d.model_id}</td>
                          <td style={{ fontSize: 12 }}>{reasonText(d.reason)}</td>
                          <td>
                            <span className={`badge ${d.mode === 'enforce' ? 'badge-ok' : 'badge-off'}`}>{d.mode}</span>
                            {d.mode !== 'enforce' && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 6 }}>{t(G.autoRouting.actual, { m: d.actual_model })}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ops-toolbar" style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{t(G.autoRouting.statsTitle)}</div>
        <select className="cat-select" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          {[1, 6, 24, 72, 168].map((h) => (
            <option key={h} value={h}>
              {t(G.autoRouting.hoursOpt, { h })}
            </option>
          ))}
        </select>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            stats.reload()
            shadow.reload()
          }}
        >
          <IconRefresh size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          {t(G.autoRouting.refresh)}
        </button>
      </div>
      <div className="table-wrap" style={{ marginBottom: 16 }}>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.autoRouting.colScene)}</th>
                <th>{t(G.autoRouting.colMode)}</th>
                <th>{t(G.autoRouting.colReason)}</th>
                <th>{t(G.autoRouting.colResolved)}</th>
                <th>{t(G.autoRouting.colRequests)}</th>
                <th>{t(G.autoRouting.colDecision)}</th>
              </tr>
            </thead>
            <tbody>
              {(stats.data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty" style={{ textAlign: 'center', padding: 28, color: 'var(--text-tertiary)' }}>
                    {stats.loading ? t(G.common.loading) : t(G.autoRouting.statsEmpty)}
                  </td>
                </tr>
              ) : (
                (stats.data?.data ?? []).map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.scene_id}</td>
                    <td>
                      <span className={`badge ${r.mode === 'enforce' ? 'badge-ok' : 'badge-off'}`}>{r.mode}</span>
                    </td>
                    <td style={{ fontSize: 12 }}>{reasonText(r.reason)}</td>
                    <td className="mono">{r.resolved_model}</td>
                    <td>{r.requests.toLocaleString()}</td>
                    <td>{r.avg_decision_us ? `${r.avg_decision_us.toLocaleString()} µs` : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ops-toolbar" style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{t(G.autoRouting.shadowTitle)}</div>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(G.autoRouting.shadowHint)}</span>
        <span style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
          <input type="checkbox" disabled={!isAdmin} checked={cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} style={{ width: 15, height: 15 }} />
          {t(G.autoRouting.cfgEnabled)}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
          {t(G.autoRouting.cfgRate)}
          <input
            className="form-input"
            type="number"
            min={0}
            max={1}
            step={0.05}
            disabled={!isAdmin}
            style={{ width: 80, padding: '5px 8px' }}
            value={cfg.sample_rate}
            onChange={(e) => setCfg((c) => ({ ...c, sample_rate: Number(e.target.value) }))}
          />
        </label>
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" disabled={busy === 'cfg' || !shadowCfg.data?.policy_saved} title={shadowCfg.data?.policy_saved ? undefined : t(G.autoRouting.cfgNeedPolicy)} onClick={saveCfg}>
            {t(C.save)}
          </button>
        )}
      </div>
      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.autoRouting.colScene)}</th>
                <th>{t(G.autoRouting.colEvents)}</th>
                <th>{t(G.autoRouting.colSuccess)}</th>
                <th>{t(G.autoRouting.colAgreement)}</th>
                <th>{t(G.autoRouting.colPrimary)}</th>
                <th>{t(G.autoRouting.colShadowLat)}</th>
                <th>{t(G.autoRouting.colCross)}</th>
              </tr>
            </thead>
            <tbody>
              {(shadow.data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty" style={{ textAlign: 'center', padding: 28, color: 'var(--text-tertiary)' }}>
                    {shadow.loading ? t(G.common.loading) : t(G.autoRouting.shadowEmpty)}
                  </td>
                </tr>
              ) : (
                (shadow.data?.data ?? []).map((r) => (
                  <tr key={r.scene_id}>
                    <td className="mono">{r.scene_id}</td>
                    <td>{r.events.toLocaleString()}</td>
                    <td>{pctText(r.success_rate)}</td>
                    <td>{pctText(r.agreement_rate)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {latText(r.primary_p50_ms)} / {latText(r.primary_p95_ms)}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {latText(r.shadow_p50_ms)} / {latText(r.shadow_p95_ms)}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {Object.entries(r.cross)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join('  ')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </OpsPage>
  )
}
